import { format } from 'date-fns';
import { TFunction } from 'i18next';
import { getFixedNumber } from 'utils/general';

import { ISettlements } from '@/modules/Settlements/types';
import { formatAccountingPeriod } from '@/shared/components/molecules/CreateSettlement/logic';
import { getLocalizedDate } from '@/shared/lib/dates';
// TODO: just create a hook & refactor all functions

const workerEmptyRow = {
  date: '',
  basePay: '',
  totalBonuses: '',
  totalDeductions: '',
  totalHours: '',
  totalOperations: '',
  totalOvertime: '',
  totalPay: '',
};
const emptyRow = {
  date: '',
  type: '',
  jobInstanceId: '',
  totalWorkers: '',
  place: '',
  supervisorName: '',
  jobInstanceName: '',
  supplierAssignmentId: '',
  assignmentJobName: '',
  pricingMethod: '',
  payUnit: '',
  suppAssPrice: '',
  unitType: '',
  expectedProductivityTarget: '',
  totalUnitsAchieved: '',
  supervisionBonuses: '',
  supervisionDeduction: '',
  numberHoursBonuses: '',
  totalHoursBonuses: '',
  averagePayUnitsPerWorker: '',
  totalPayUnits: '',
  netPayment: '',
};

export const getWorkerSerializedXLSXData = ({
  currency,
  t,
  data,
  farm,
  language,
}: {
  t: TFunction;
  data?: ISettlements.IGetWorkerSettlementDetails;
  currency: string;
  language: string;
  farm?: string;
}) => {
  const serializedData: ISettlements.IWorkerSettlementDaySchema[] = [];

  serializedData.push(
    getWorkerSettlementTitleRow(
      t,
      data?.id
    ) as unknown as ISettlements.IWorkerSettlementDaySchema
  );

  serializedData.push(
    getWorkerSettlementTitleValuesRow({
      farm,
      workerName: data?.workerName,
      crewName: data?.crewName || '',
      startDate: data?.startDate || '',
      endDate: data?.endDate || '',
      issuanceDate: data?.issuanceDate || '',
      language,
      t,
    }) as unknown as ISettlements.IWorkerSettlementDaySchema
  );
  serializedData.push({
    settlementId: `${t('accounting-period')}: `,
    issuanceDate: formatAccountingPeriod({
      language,
      startDate: data?.startDate || '',
      endDate: data?.endDate || '',
    }),
    farm: `${t('worker')}: ${data?.workerName}`,
  } as unknown as ISettlements.IWorkerSettlementDaySchema);
  // add empty row
  serializedData.push({} as unknown as ISettlements.IWorkerSettlementDaySchema);

  serializedData.push(
    getWorkerHeaders(
      t,
      currency
    ) as unknown as ISettlements.IWorkerSettlementDaySchema
  );

  if (data?.days)
    for (let i = 0; i < data?.days?.length; i++) {
      serializedData.push({
        ...data.days[i],
        date: format(new Date(data.days[i].date), 'dd MMM yyyy'),
      } as unknown as ISettlements.IWorkerSettlementDaySchema);
    }

  // footer row total amount
  serializedData.push({
    ...workerEmptyRow,
    date: t('total-settlement'),
    totalPay: getFixedNumber(data?.totalAmount),
  } as unknown as ISettlements.IWorkerSettlementDaySchema);

  return serializedData.flat();
};
export const getSerializedXLSXData = ({
  data,
  t,
  currency,
  language,
  farm,
}: {
  t: TFunction;
  data?: ISettlements.IGetSettlementDetails;
  currency: string;
  language: string;
  farm?: string;
}): ISettlements.ISettlementDetailsDayInstance[] => {
  // inject in every day-> date in first instance and supplier deduction in last instance
  const serializedData: ISettlements.ISettlementDetailsDayInstance[] = [];
  // add head titles
  serializedData.push(
    getSettlementTitleRow(
      t
    ) as unknown as ISettlements.ISettlementDetailsDayInstance
  );
  // add empty row
  serializedData.push(
    getSettlementTitleValuesRow({
      farm,
      supplierName: data?.supplierName || '',
      startDate: data?.startDate || '',
      endDate: data?.endDate || '',
      issuanceDate: data?.issuanceDate || '',
      id: data?.id || '',
      language,
    }) as unknown as ISettlements.ISettlementDetailsDayInstance
  );
  // add empty row
  serializedData.push(
    {} as unknown as ISettlements.ISettlementDetailsDayInstance
  );
  // table data
  // add headers then add rows of data
  serializedData.push(
    getHeaders(
      t,
      currency
    ) as unknown as ISettlements.ISettlementDetailsDayInstance
  );
  if (data?.days)
    for (let i = 0; i < data?.days?.length; i++) {
      if (data.days[i].instances.length > 0) {
        // get all instances in the day and add date (attribute) to every instance then
        // then arrange them in array and pick only data that we need to show in the excel sheet
        // note:order is very important
        // eslint-disable-next-line no-unsafe-optional-chaining
        const newDayInstances = [...data?.days[i].instances].map((item) => ({
          date: '',
          type: t('main'),
          jobInstanceId: item?.jobInstanceId || '-',
          totalWorkers: item.totalWorkers || '-',
          place: item.place || '-',
          supervisorName: item.supervisorName || '-',
          jobInstanceName: item.jobInstanceName || '-',
          supplierAssignmentId: item.supplierAssignmentId || '-',
          assignmentJobName: item.assignmentJobName || '-',
          pricingMethod: t(
            item.pricingMethod === 'unit' ? 'rate' : item.pricingMethod || '-'
          ),
          payUnit: item.payUnit === 'daily' ? t('daily') : item.payUnit || '-',
          suppAssPrice: item.suppAssPrice || '-',
          unitType:
            item.category === 'WASTED-TIME' ? '-' : item.unitType || '-',
          expectedProductivityTarget: item.expectedProductivityTarget || '-',
          totalUnitsAchieved:
            item.category === 'WASTED-TIME'
              ? '-'
              : `${item.totalUnitsAchieved || '-'} ${item.unitType || '-'}`,
          supervisionBonuses: item.supervisionBonuses || '-',
          supervisionDeduction: item.supervisionDeduction || '-',
          numberHoursBonuses: item.numberHoursBonuses || '-',
          totalHoursBonuses: item.totalHoursBonuses || '-',
          averagePayUnitsPerWorker:
            getFixedNumber(item.averagePayUnitsPerWorker) || 0,
          totalPayUnits: item.totalPayUnits || '-',
          netPayment: item.netPayment || '-',
        }));
        // add administrative deduction and bonus at the end of the day
        newDayInstances.push({
          ...emptyRow,
          type: t('administrative-deduction'),
          netPayment: getFixedNumber(
            data?.days[i].totalAdministrativeDeduction || 0
          ),
        });
        newDayInstances.push({
          ...emptyRow,
          type: t('administrative-bonus'),
          netPayment: getFixedNumber(
            data?.days[i].totalAdministrativeBonuses || 0
          ),
        });

        // add date to all instances
        // for (let k = 0; k < newDayInstances?.length; k++) {
        //   newDayInstances[k] = { ...newDayInstances[k], date: data?.days[i].day };
        // }
        // add date to the first instance
        newDayInstances[0] = { ...newDayInstances[0], date: data?.days[i].day };

        // add serilized instance data to the main array
        serializedData.push(
          newDayInstances as unknown as ISettlements.ISettlementDetailsDayInstance
        );
      }
    }
  // footer row total amount
  serializedData.push({
    ...emptyRow,
    date: t('net-settlement'),
    netPayment: getFixedNumber(data?.totalAmount),
  } as unknown as ISettlements.ISettlementDetailsDayInstance);

  return serializedData.flat();
};
const getWorkerHeaders = (t: TFunction, currency: string) => ({
  date: t('date'),
  totalHours: t('total-hours'),
  totalOperations: t('total-operations'),
  basePay: `${t('base-pay')} (${currency})`,
  totalBonuses: `${t('bonus')} (${currency})`,
  totalDeductions: `${t('deduction')} (${currency})`,
  totalOvertime: `${t('OVERTIME')} (${currency})`,
  totalPay: `${t('total-pay')} (${currency})`,
});
const getHeaders = (t: TFunction, currency: string) => ({
  date: t('date'),
  type: t('categorize-settlement'),
  jobInstanceId: t('job-order'),
  totalWorkers: t('attendance'),
  place: t('location'),
  supervisorName: t('supervisorName'),
  jobInstanceName: t('executed-job'),
  supplierAssignmentId: t('contract-code'),
  assignmentJobName: t('contract-job'),
  pricingMethod: t('contract-form'),
  payUnit: t('pay-unit'),
  suppAssPrice: `${t('pay-unit-value')} (${currency})`,
  unitType: t('productivity-unit'),
  expectedProductivityTarget: t('productivity-target-per-worker'),
  totalUnitsAchieved: t('actual-productivity-per-team'),
  supervisionBonuses: `${t('supervisionBonuses')} (${currency})`,
  supervisionDeduction: `${t('supervisionDeduction')} (${currency})`,
  numberHoursBonuses: t('number-extra-hours'),
  totalHoursBonuses: `${t('value-extra-hours')} (${currency})`,
  averagePayUnitsPerWorker: t('average-pay-unit-per-worker'),
  totalPayUnits: t('total-number-pay-units'),
  netPayment: `${t('total-dues')} (${currency})`,
});

export const getWorkerSettlementTitleRow = (
  t: TFunction,
  settlementId?: string
) => ({
  settlementId: `${t('settlement')} ${settlementId}`,
  issuanceDate: '',
  farm: t('farm'),
  workerName: t('worker'),
  crewName: t('crew'),
  accountingPeriod: t('accounting-period'),
});
export const getWorkerSettlementTitleValuesRow = ({
  farm,
  startDate,
  endDate,
  issuanceDate,
  language,
  t,
  crewName,
  workerName,
}: IGetWorkerSettlementTitleValues) => ({
  settlementId: `${t('issuing-date')}: `,
  issuanceDate: getLocalizedDate({
    date: issuanceDate,
    locale: language,
    formatDate: 'yyyy-MM-dd',
  }),
  farm,
  workerName: workerName || '-',
  crewName: crewName || '-',
  accountingPeriod: formatAccountingPeriod({
    startDate,
    endDate,
    language,
  }),
});
export const getSettlementTitleRow = (t: TFunction) => ({
  id: t('settelment-Id'),
  empty1: '',
  farm: t('farm'),
  empty2: '',
  supplierName: t('team_leader'),
  empty3: '',
  accountingPeriod: t('accounting-period'),
  empty4: '',
  issuanceDate: t('issuing-date'),
});

interface IGetWorkerSettlementTitleValues {
  workerName?: string;
  crewName: string;
  startDate: string;
  endDate: string;
  issuanceDate: string;
  farm?: string;
  language: string;
  t: TFunction;
}
interface IGetSettlementTitleValues {
  supplierName: string;
  startDate: string;
  endDate: string;
  issuanceDate: string;
  id: string | number;
  farm?: string;
  language: string;
}
export const getSettlementTitleValuesRow = ({
  farm,
  supplierName,
  startDate,
  endDate,
  issuanceDate,
  id,
  language,
}: IGetSettlementTitleValues) => ({
  id,
  empty1: '',
  farm,
  empty2: '',
  supplierName,
  empty3: '',
  accountingPeriod: formatAccountingPeriod({
    startDate,
    endDate,
    language,
  }),
  empty4: '',
  issuanceDate: getLocalizedDate({
    date: issuanceDate,
    locale: language,
    formatDate: 'yyyy-MM-dd',
  }),
});
